import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, AlertTriangle } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useCustomer } from '@/hooks/useCustomers'
import { formatDate, planLabel } from '@/lib/customer-format'
import { TrialStatusBadge } from '@/components/customers/TrialStatusBadge'
import { CustomerOverview } from '@/components/customers/CustomerOverview'
import { CustomerVehicles } from '@/components/customers/CustomerVehicles'
import { CustomerUsers } from '@/components/customers/CustomerUsers'
import { CustomerComplianceSection } from '@/components/customers/CustomerComplianceSection'
import { CustomerOnboardingSection } from '@/components/customers/CustomerOnboardingSection'
import { AdditionalDetails } from '@/components/customers/AdditionalDetails'
import { DeferredWidgetCard } from '@/components/customers/DeferredWidgetCard'
import { FounderCallbackBadge } from '@/components/customers/FounderCallbackBadge'

function BackLink() {
  return (
    <Link
      to="/dashboard/customers"
      className="inline-flex items-center gap-1.5 font-label text-xs uppercase tracking-wider text-cmd-outline transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
      All customers
    </Link>
  )
}

export function CustomerDetail() {
  const { tenantId } = useParams<{ tenantId: string }>()
  const { data, isLoading, isError } = useCustomer(tenantId)

  if (isLoading) {
    return (
      <div className="space-y-4">
        <BackLink />
        <Skeleton className="h-10 w-72" />
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  // Error, bridge-unavailable, or unknown tenant all land here — the bridge
  // can't distinguish "not found" from "unreachable", so we say both honestly.
  if (isError || !data || !data.available || !data.customer) {
    return (
      <div className="space-y-4">
        <BackLink />
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <AlertTriangle className="h-7 w-7 text-[#ffd080]" aria-hidden="true" />
            <p className="font-label text-sm uppercase tracking-wider text-white/80">
              Couldn't load this customer
            </p>
            <p className="max-w-md text-sm text-cmd-outline">
              This customer no longer exists, or the TMAgent product bridge is
              unavailable right now. Go back to the list and try again.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const { customer } = data
  const { account } = customer

  return (
    <div className="space-y-5">
      <BackLink />

      {/* Header */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-sans text-2xl font-semibold tracking-tight text-white">
            {account.companyName}
          </h2>
          {account.founderCallbackRequested === true && <FounderCallbackBadge size="lg" />}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-cmd-outline">
          <span className="text-white/80">{planLabel(account.plan)}</span>
          <TrialStatusBadge status={account.trialStatus} daysRemaining={null} />
          <span aria-hidden="true">·</span>
          <span>Signed up {formatDate(account.signupDate)}</span>
        </div>
      </div>

      {/* Overview: account facts + billing summary */}
      <CustomerOverview account={account} billing={customer.billing} />

      {/* Vehicles */}
      <CustomerVehicles vehicles={customer.vehicles} />

      {/* Users + Compliance */}
      <div className="grid gap-4 lg:grid-cols-2">
        <CustomerUsers users={customer.users} />
        <CustomerComplianceSection alerts={customer.complianceAlerts} />
      </div>

      {/* Onboarding + auto-shown additional details */}
      <div className="grid gap-4 lg:grid-cols-2">
        <CustomerOnboardingSection onboarding={customer.onboarding} />
        <AdditionalDetails fields={account.additionalFields} variant="card" />
      </div>

      {/* Deferred telemetry widgets — honest placeholders, never fake data */}
      <div className="grid gap-4 lg:grid-cols-2">
        <DeferredWidgetCard
          title="Agent activity"
          description="A per-tenant feed of agent runs against this customer will appear here once agent-activity telemetry is wired through the bridge."
        />
        <DeferredWidgetCard
          title="Health score trend"
          description="A health-score trend chart will appear here once health telemetry is captured over time."
        />
      </div>
    </div>
  )
}
